import { ResourceActivityResourceType } from './database';

export const VERSIONED_RESOURCE_TYPES = [
    'dashboards',
    'folders',
    'importedEntities',
    'investments',
    'listViews',
    'lists',
    'memos',
    'pipelines',
    'reportComponents',
    'reports',
    'tasks',
    'teams',
] as const;

export type VersionedResourceType = (typeof VERSIONED_RESOURCE_TYPES)[number];

export interface ResourceHistoryTarget {
    activityId?: string;
    resourceId: string;
    resourceType: VersionedResourceType;
    title?: string;
}

export interface ResourceVersionActor {
    avatar?: string;
    emailAddress?: string;
    firstName?: string;
    id: string;
    lastName?: string;
}

export interface ResourceVersionChange {
    after?: unknown;
    before?: unknown;
    path: string;
    type: 'added' | 'removed' | 'changed';
}

export interface ResourceHistoryPolicy {
    enabled: boolean;
    minimumPlan: 'plus';
    retentionDays: number | null;
    tier: string;
}

export interface ResourceVersionListItem {
    activityId: string;
    actor?: ResourceVersionActor;
    changes?: ResourceVersionChange[];
    checksum: string;
    createdAt: string;
    eventType?: string;
    id: string;
    locked: boolean;
    restoredFromVersionId?: string;
    snapshot?: Record<string, unknown>;
    title?: string;
}

export interface ResourceHistoryPage {
    hasMore: boolean;
    items: ResourceVersionListItem[];
    nextCursor?: string;
    policy: ResourceHistoryPolicy;
}

export const MERGEABLE_RESOURCE_TYPES = [
    'dashboards',
    'folders',
    'investments',
    'lists',
    'memos',
    'pipelines',
    'reports',
    'tasks',
    'teams',
] as const satisfies readonly VersionedResourceType[];

export type MergeableResourceType = (typeof MERGEABLE_RESOURCE_TYPES)[number];

export interface ResourceMergePreview {
    changes: ResourceVersionChange[];
    mergedSnapshot: Record<string, unknown>;
    sourceChecksum: string;
    sourceSnapshot: Record<string, unknown>;
    targetChecksum: string;
    targetSnapshot: Record<string, unknown>;
}

export interface ResourceMergeInput {
    archiveSource: boolean;
    expectedSourceChecksum: string;
    expectedTargetChecksum: string;
    resourceType: MergeableResourceType;
    sourceId: string;
    targetId: string;
}

export interface ResourceMergeResult {
    changes: ResourceVersionChange[];
    mergedSnapshot: Record<string, unknown>;
    sourceArchived: boolean;
}

export const isMergeableResourceType = (resourceType: VersionedResourceType): resourceType is MergeableResourceType =>
    MERGEABLE_RESOURCE_TYPES.some((candidate) => candidate === resourceType);

export const isVersionedResourceType = (
    resourceType: ResourceActivityResourceType,
): resourceType is VersionedResourceType => {
    return VERSIONED_RESOURCE_TYPES.some((candidate) => candidate === resourceType);
};
